<template>
  <Pane
    :value="documentsTablePane.isOpen"
    temporary
    class="DocumentsTablePane"
    data-locator="documents-filders-pane"
    :tabs="tabs"
    v-model:current-tab="tab"
    @update:modelValue="onInput"
  >
    <template #fixed-header>
      <DocumentSelection
        :selected-documents="documentsTablePane.documents"
        removable
        @remove="removeDocument"
      />
      <v-divider class="my-6" />
    </template>
    <template #content-share>
      <DocumentShare :documents="documentsTablePane.documents" :force-pm-selection="true" />
    </template>
    <template #content-move>
      <!-- This component has to share its lifecycle with the pane open state -->
      <!-- because we need to fully reset its state when closing -->
      <!-- See V2-1160 for one of the things that happens when not doing that. -->
      <DocumentsPaneMove v-if="documentsTablePane.isOpen"/>
    </template>
    <template #content-duplicate>
      <DocumentsPaneDuplicate v-if="documentsTablePane.isOpen"/>
    </template>
  </Pane>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import DocumentSelection from '@/common/document-selection/DocumentSelection.vue'
import DocumentShare from '@/common/document-share/DocumentShare.vue'
import Pane from '@/common/Pane.vue'
import {
  CHANGE_TABLE_DOCUMENTS_PANE_TAB,
  CLOSE_TABLE_DOCUMENTS_PANE,
  DESELECT_ALL_DOCUMENTS,
  UNSELECT_DOCUMENT,
} from '@/store/modules/documents/action_types'

import DocumentsPaneDuplicate from './DocumentsPaneDuplicate.vue'
import DocumentsPaneMove from './DocumentsPaneMove.vue'

export default {
  name: 'DocumentsTablePane',
  components: {
    DocumentShare,
    DocumentSelection,
    DocumentsPaneDuplicate,
    DocumentsPaneMove,
    Pane,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState('documents', ['documentsTablePane', 'numberingEnabled']),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('user', ['currentUserId']),
    ...mapGetters('groups', ['currentUserGroupId']),
    tab: {
      get () {
        return this.documentsTablePane.tab
      },
      set (val) {
        this.CHANGE_TABLE_DOCUMENTS_PANE_TAB(val)
      },
    },
    tabs () {
      const tabs = []
      if (this.isCurrentUserPm) {
        tabs.push(
          {
            title: this.$t('project.documents.pane.DocumentsTablePane.share'),
            id: 'share',
          },
        )
      }
      if (this.canWriteAllFiles) {
        tabs.push({
          title: this.$t('project.documents.pane.DocumentsTablePane.move'),
          id: 'move',
        })
      }
      if (this.documentsTablePane.documents.length === 1 && this.documentsTablePane.documents[0].type === 'file') {
        tabs.push({
          title: this.$t('project.documents.pane.DocumentsTablePane.duplicate'),
          id: 'duplicate',
        })
      }
      return tabs
    },
    canWriteAllFiles () {
      return this.documentsTablePane?.documents.every(doc => {
        if (doc.canWrite) {
          return true
        } else {
          return doc.rights?.write?.groups?.find((group) => group.id === this.currentUserGroupId) ||
            doc.rights?.write?.users?.find((user) => user.id === this.currentUserId)
        }
      })
    },
  },
  methods: {
    ...mapActions('documents', [
      CHANGE_TABLE_DOCUMENTS_PANE_TAB,
      CLOSE_TABLE_DOCUMENTS_PANE,
      DESELECT_ALL_DOCUMENTS,
      UNSELECT_DOCUMENT,
    ]),
    removeDocument (documentId) {
      this.UNSELECT_DOCUMENT(documentId)
    },
    onInput (value) {
      if (!value) {
        this.CLOSE_TABLE_DOCUMENTS_PANE()
      }
    },
  },
}
</script>

<style lang="scss">
  .Document-name {
    max-width: 350px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .Document-chip {
    .v-chip__close:hover {
      color: rgb(var(--v-error-base)) !important;
    }
  }
</style>
